import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AdminBodyProps,
  IAdminPartner,
  IAdminRecipient,
  IAdmins,
  IAdminsOBCheck,
  IAdminUserAccessUpdate,
  IAdminUserUpdate,
} from 'store/admins/types';
import API_ROUTES from 'services/apiRoutes';
import { baseQueryWithAuth } from 'services/baseQueryWithAuth';

export const adminsApi = createApi({
  reducerPath: 'adminsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Recipients', 'Partners', 'AdminDetail', 'OBCheck', 'AdminsList'],
  endpoints: builder => ({
    getAdminsRecipients: builder.query<IAdminRecipient[], void>({
      query: () => API_ROUTES.ADMINS_RECIPIENTS(),
      transformResponse: (response: { recipients: IAdminRecipient[] } | IAdminRecipient[]) => {
        return Array.isArray(response) ? response : response.recipients;
      },
      providesTags: ['Recipients'],
    }),

    getAdminsPartners: builder.query<IAdminPartner[], void>({
      query: () => API_ROUTES.ADMINS_PARTNERS(),
      transformResponse: (response: { partners: IAdminPartner[] } | IAdminPartner[]) => {
        return Array.isArray(response) ? response : response.partners;
      },
      providesTags: ['Partners'],
    }),

    getAdminUsersList: builder.query<IAdmins[], { search_str?: string }>({
      query: ({ search_str = '' }) => API_ROUTES.ADMIN_USERS(search_str),
      transformResponse: (response: { admin_users: IAdmins[] } | IAdmins[]) => {
        const adminUsers = Array.isArray(response) ? response : response.admin_users;
        return adminUsers.map((user: any) => ({
          ...user,
          id: user.id ?? '',
          email: user.email ?? '',
          admin_status: user.admin_status ?? 'ACTIVE',
          role_names: user.role_names ?? (user.role_type ? [user.role_type] : []),
          resource_access: user.resource_access ?? {},
        }));
      },
      providesTags: ['AdminsList'],
    }),

    getAdminUserDetail: builder.query<IAdmins, { adminId: string }>({
      query: ({ adminId }) => API_ROUTES.GET_ADMIN_USER(adminId),
      providesTags: ['AdminDetail'],
    }),

    createAdmin: builder.mutation<any, { body: AdminBodyProps }>({
      query: ({ body }) => ({
        url: API_ROUTES.ADMIN_USERS(''),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AdminsList'],
    }),

    checkOnboardingStatus: builder.query<IAdminsOBCheck, string>({
      query: recipientId => API_ROUTES.ADMINS_OB_CHECK(recipientId),
      providesTags: ['OBCheck'],
    }),

    startOnboarding: builder.mutation<any, { recipient_id: string; onboarding_status: string }>({
      query: body => ({
        url: API_ROUTES.ADMINS_OB,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OBCheck'],
    }),

    completeOnboardingForm: builder.mutation<void, { recipientId: string }>({
      query: ({ recipientId }) => ({
        url: API_ROUTES.ADMINS_ONBOARDING_FORM_COMPLETE(recipientId),
        method: 'PUT',
        body: { onboarding_form_complete: true },
      }),
    }),

    getOnboardingLink: builder.mutation<{ url: string }, string>({
      query: recipientId => ({
        url: API_ROUTES.ACCOUNTS_LINK,
        method: 'POST',
        body: { recipient_id: recipientId },
      }),
      invalidatesTags: ['OBCheck'],
    }),

    patchAdminUser: builder.mutation<any, IAdminUserUpdate>({
      query: body => ({
        url: API_ROUTES.ADMINS_USERS,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['OBCheck'],
    }),

    patchAdminUserAccess: builder.mutation<any, IAdminUserAccessUpdate>({
      query: body => ({
        url: API_ROUTES.ADMINS_USERS_ACCESS(body.admin_user_id),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminDetail', 'OBCheck', 'AdminsList'],
    }),
  }),
});

export const {
  useGetAdminsRecipientsQuery,
  useGetAdminsPartnersQuery,
  useGetAdminUsersListQuery,
  useGetAdminUserDetailQuery,
  useCreateAdminMutation,
  useCheckOnboardingStatusQuery,
  useLazyCheckOnboardingStatusQuery,
  useStartOnboardingMutation,
  useCompleteOnboardingFormMutation,
  useGetOnboardingLinkMutation,
  usePatchAdminUserMutation,
  usePatchAdminUserAccessMutation,
} = adminsApi;
